import {
  ELinkColorVariant,
  ELinkVariant,
  ETextStyleVariant,
  Link,
  Text,
} from '@outdoorsyco/bonfire';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

// useLayoutEffect logs an SSR warning since it has no server equivalent.
// Fall back to useEffect on the server; the measurement only runs in the browser anyway.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

interface IExpandableDescriptionProps {
  text?: string | null;
  textVariant?: ETextStyleVariant;
  className?: string;
}

export const ExpandableDescription: React.FC<IExpandableDescriptionProps> = ({
  text,
  textVariant = ETextStyleVariant.SmallRegular,
  className,
}) => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpand, setNeedsExpand] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;
    setNeedsExpand(el.scrollHeight > el.clientHeight + 1);
  }, [text]);

  if (!text) return null;

  const showMoreLabel = intl.formatMessage({
    id: 'aGD0Ps',
    defaultMessage: 'Show more',
    description: 'Host add-ons > Expand description button label',
  });
  const showLessLabel = intl.formatMessage({
    id: 'dyVzxw',
    defaultMessage: 'Show less',
    description: 'Host add-ons > Collapse description button label',
  });

  const handleToggle = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(prev => !prev);
  };

  return (
    <div className={className}>
      <div ref={textRef} className={isExpanded ? '' : 'line-clamp-2'}>
        <Text variant={textVariant}>{text}</Text>
      </div>
      {needsExpand && (
        <div className="mt-1">
          <Link
            label={isExpanded ? showLessLabel : showMoreLabel}
            variant={ELinkVariant.Underline}
            textVariant={ETextStyleVariant.SmallRegular}
            colorVariant={ELinkColorVariant.Secondary}
            href="#"
            onClick={handleToggle}
          />
        </div>
      )}
    </div>
  );
};
